import React, { useMemo, useRef, useState } from 'react';
import { Box, Tabs, Tab, Paper, Typography, IconButton, Stack, Divider } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export type ReportResponse = {
  status?: boolean;
  data?: {
    success?: boolean;
    report_name?: string;
    generated_at?: string;
    formats?: {
      json?: {
        template_id?: string;
        template_name?: string;
        generated_at?: string;
        parameters?: Record<string, any>;
        charts?: Array<{
          id: string;
          title?: string;
          result?: { message?: string; data?: any[] };
        }>;
        metadata?: Record<string, any>;
      };
      html_tabular?: string | null;
      html_graph?: string | null;
      excel_base64?: string | null;
      formats_available?: {
        json?: boolean;
        html_tabular?: boolean;
        html_graph?: boolean;
        excel?: boolean;
      };
    };
  };
};

type View = 'json' | 'table' | 'graph';

interface ReportViewerProps {
  response: ReportResponse;
  defaultView?: View;
  height?: number | string;
}

const normalizeResponse = (incoming: ReportResponse | any): ReportResponse => {
  if (incoming?.data?.formats) return incoming as ReportResponse;
  const quick = incoming?.data;
  if (quick && (quick.charts || quick.metadata || quick.template_name || quick.generated_at)) {
    return {
      status: incoming?.status ?? true,
      data: {
        success: true,
        report_name: quick.template_name ?? quick.template_id ?? 'Report',
        generated_at: quick.generated_at,
        formats: {
          json: {
            template_id: quick.template_id ?? undefined,
            template_name: quick.template_name ?? undefined,
            generated_at: quick.generated_at ?? undefined,
            parameters: quick.parameters ?? {},
            charts: quick.charts ?? [],
            metadata: quick.metadata ?? {},
          },
          html_tabular: quick.html_tabular ?? null,
          html_graph: quick.html_graph ?? null,
          excel_base64: quick.excel_base64 ?? null,
          formats_available: {
            json: true,
            html_tabular: !!quick?.html_tabular,
            html_graph: !!quick?.html_graph,
            excel: !!quick?.excel_base64,
          },
        },
      },
    };
  }
  return { status: false, data: { success: false, formats: {} as any } };
};

const AutoHeightIframe: React.FC<{
  html: string;
  minHeight?: number;
  maxHeight?: number | string;
}> = ({ html, minHeight = 500, maxHeight = '80vh' }) => {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [h, setH] = useState<number>(minHeight);

  const onLoad = () => {
    const iframe = ref.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      const body = doc?.body;
      if (body) {
        const measure = () => {
          const newH = Math.max(body.scrollHeight, body.offsetHeight, minHeight);
          setH(newH);
        };
        measure();
        setTimeout(measure, 300);
        setTimeout(measure, 1000);
        setTimeout(measure, 2000);
      }
    } catch {}
  };

  return (
    <iframe
      ref={ref}
      srcDoc={html}
      onLoad={onLoad}
      style={{ width: '100%', height: h, border: 0, maxHeight }}
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    />
  );
};

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
    <Typography variant="body2">{children}</Typography>
  </Box>
);

const PrettyJson: React.FC<{ value: unknown }> = ({ value }) => {
  const text = useMemo(() => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return 'Invalid JSON';
    }
  }, [value]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <IconButton size="small" onClick={handleCopy} aria-label="Copy JSON">
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          bgcolor: '#0b1021',
          color: '#e6edf3',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: '70vh',
          fontSize: 12.5,
          lineHeight: 1.5,
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

const TitleBar: React.FC<{ name?: string; generatedAt?: string }> = ({ name, generatedAt }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {name || 'Report'}
      </Typography>
      {generatedAt && (
        <Typography variant="caption" color="text.secondary">
          Generated: {generatedAt}
        </Typography>
      )}
    </Box>
  </Stack>
);

const viewOrder: View[] = ['json', 'table', 'graph'];

const resolveInitialView = (res: ReportResponse, pref?: View): View => {
  const avail = {
    json: !!res?.data,
    table: !!res?.data?.formats?.html_tabular,
    graph: !!res?.data?.formats?.html_graph,
  };
  if (pref && avail[pref]) return pref;
  for (const v of viewOrder) {
    if (v === 'json' && avail.json) return 'json';
    if (v === 'table' && avail.table) return 'table';
    if (v === 'graph' && avail.graph) return 'graph';
  }
  return 'json';
};

const ReportViewer: React.FC<ReportViewerProps> = ({ response, defaultView, height = 'auto' }) => {
  const normalized = useMemo(() => normalizeResponse(response), [response]);
  const reportName = normalized.data?.report_name ?? normalized.data?.formats?.json?.template_name;
  const generatedAt = normalized.data?.generated_at ?? normalized.data?.formats?.json?.generated_at;
  const [view, setView] = useState<View>(() => resolveInitialView(normalized, defaultView));
  const handleTabChange = (_: React.SyntheticEvent, v: View) => setView(v);

  const htmlTabular = normalized.data?.formats?.html_tabular ?? null;
  const htmlGraph = normalized.data?.formats?.html_graph ?? null;

  return (
    <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden' }}>
      <TitleBar name={reportName} generatedAt={generatedAt} />
      <Divider />
      <Tabs value={view} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ px: 1 }}>
        <Tab value="json" label="JSON" />
        <Tab value="table" label="Table" />
        <Tab value="graph" label="Graph" />
      </Tabs>
      <Divider />
      <Box sx={{ p: 2 }}>
        {view === 'json' && (
          <PrettyJson value={normalized.data?.formats?.json ?? normalized.data} />
        )}

        {view === 'table' &&
          (htmlTabular ? (
            <AutoHeightIframe html={htmlTabular} />
          ) : (
            <EmptyState>No Table view available.</EmptyState>
          ))}

        {view === 'graph' &&
          (htmlGraph ? (
            <AutoHeightIframe html={htmlGraph} />
          ) : (
            <EmptyState>No Graph view available.</EmptyState>
          ))}
      </Box>
    </Paper>
  );
};

export default ReportViewer;
